import { useState, useEffect, useRef } from 'react';

/**
 * SearchableSelect — dropdown with text search filter.
 *
 * Props:
 *   label         — optional label shown above the control
 *   options       — [{ value, label }]
 *   closedOptions — optional [{ value, label }], same shape as `options` — rendered
 *                   in a collapsed-by-default "Closed (N)" section below `options`
 *                   instead of mixed into the flat list. Omit (or pass []) for the
 *                   original flat-list behavior — fully backward compatible. A
 *                   search query matching something in this group auto-expands it,
 *                   so searching never hides a real match behind a collapsed toggle.
 *   value         — currently selected value (compared as String) — looked up in
 *                   both `options` and `closedOptions`, so a value that happens to
 *                   be closed still displays its real label on the trigger, not the
 *                   placeholder.
 *   onChange      — called with the selected value (or '' to clear)
 *   placeholder   — text shown when nothing is selected
 */
export default function SearchableSelect({ label, options, closedOptions = [], value, onChange, placeholder = '— search or select —' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find(o => String(o.value) === String(value))
    || closedOptions.find(o => String(o.value) === String(value));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => String(o.label ?? '').toLowerCase().includes(query.toLowerCase()));
  const filteredClosed = closedOptions.filter(o => String(o.label ?? '').toLowerCase().includes(query.toLowerCase()));
  // A non-empty query matching a closed-only result forces the section open —
  // otherwise typing a closed program's name would silently return nothing.
  const effectiveClosedOpen = closedOpen || (!!query && filteredClosed.length > 0);

  const handleSelect = (val) => { onChange(val); setQuery(''); setOpen(false); setClosedOpen(false); };

  const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' };

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '240px' }}>
      {label && <div style={labelStyle}>{label}</div>}
      <div onClick={() => setOpen(o => !o)} style={{
        border: `1px solid ${open ? '#2980b9' : '#ccc'}`, borderRadius: '4px',
        padding: '6px 10px', fontSize: '13px', background: '#fff', cursor: 'pointer',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: open ? '0 0 0 2px #d6eaf8' : 'none', minHeight: '32px',
        userSelect: 'none',
      }}>
        <span style={{ color: selected ? '#2c3e50' : '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ color: '#aaa', marginLeft: '6px', fontSize: '10px' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #2980b9', borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          maxHeight: '240px', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…" onClick={e => e.stopPropagation()}
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ddd',
                borderRadius: '3px', padding: '4px 8px', fontSize: '12px', outline: 'none' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <div onMouseDown={() => handleSelect('')}
              style={{ padding: '7px 12px', fontSize: '12px', color: '#aaa', cursor: 'pointer' }}>
              {placeholder}
            </div>
            {filtered.length === 0 && filteredClosed.length === 0
              ? <div style={{ padding: '7px 12px', fontSize: '12px', color: '#bbb' }}>No results</div>
              : filtered.map(o => (
                <div key={o.value} onMouseDown={() => handleSelect(o.value)} style={{
                  padding: '7px 12px', fontSize: '13px', cursor: 'pointer',
                  background: String(o.value) === String(value) ? '#eef4ff' : 'transparent',
                  color: String(o.value) === String(value) ? '#2980b9' : '#2c3e50',
                  fontWeight: String(o.value) === String(value) ? '600' : 'normal',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f8ff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = String(o.value) === String(value) ? '#eef4ff' : 'transparent'; }}
                >{o.label}</div>
              ))}
            {filteredClosed.length > 0 && (
              <>
                <div onMouseDown={e => { e.preventDefault(); setClosedOpen(o => !o); }} style={{
                  padding: '6px 12px', fontSize: '11px', fontWeight: '600', color: '#888', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '5px', background: '#fafafa',
                  borderTop: '1px solid #eee', userSelect: 'none',
                }}>
                  <span style={{ fontSize: '9px' }}>{effectiveClosedOpen ? '▼' : '▶'}</span>
                  Closed ({filteredClosed.length})
                </div>
                {effectiveClosedOpen && filteredClosed.map(o => (
                  <div key={o.value} onMouseDown={() => handleSelect(o.value)} style={{
                    padding: '7px 12px', fontSize: '13px', cursor: 'pointer', color: '#888',
                    background: String(o.value) === String(value) ? '#eef4ff' : 'transparent',
                    fontWeight: String(o.value) === String(value) ? '600' : 'normal',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f5f8ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = String(o.value) === String(value) ? '#eef4ff' : 'transparent'; }}
                  >{o.label}</div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
